/**
 * Pure helpers for MatchDetails (unit tested in tests/unit/utils).
 */

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

/** score_data of a match as an object (it may arrive as a JSON string). */
export const getScoreData = (match) =>
  parseMaybeJson(match?.scoreData || match?.score_data) || {};

/** match_config of a match as an object. */
export const getMatchConfig = (match) =>
  parseMaybeJson(match?.matchConfig || match?.match_config) || {};

/**
 * Per-set / per-game scores [{ p1, p2 }] stored on the server (LS-B07):
 * score_data.gameScores (written on completion and with live set state),
 * falling back to score_data.periods / setScores. Never the viewer's
 * localStorage, which only the scoring device has.
 */
export const getServerSetScores = (match) => {
  const sd = getScoreData(match);
  const list = [sd.gameScores, sd.periods, sd.setScores].find(
    (v) => Array.isArray(v) && v.length > 0
  );
  if (!list) return [];
  return list
    .filter((g) => g && typeof g === "object")
    .map((g) => ({
      p1: Number(g.p1 ?? g.participant1 ?? g.team1 ?? 0) || 0,
      p2: Number(g.p2 ?? g.participant2 ?? g.team2 ?? 0) || 0,
    }));
};

/**
 * The side ('participant1' | 'participant2') the viewer votes for on a score
 * correction. The backend's viewer.side wins (it knows team captains);
 * otherwise a user participant matches by id.
 */
export const getViewerVoteSide = (match, user) => {
  if (match?.viewer && "side" in match.viewer) {
    return match.viewer.canVote === false ? null : match.viewer.side || null;
  }
  if (!user) return null;
  const p1 = match?.participant1Id ?? match?.participant_1_id;
  const p2 = match?.participant2Id ?? match?.participant_2_id;
  const t1 = match?.participant_1_type || match?.participant1Type || "user";
  const t2 = match?.participant_2_type || match?.participant2Type || "user";
  if (t1 === "user" && Number(user.id) === Number(p1)) return "participant1";
  if (t2 === "user" && Number(user.id) === Number(p2)) return "participant2";
  return null;
};

/**
 * Finalizing is blocked while a score correction waits for votes (MT-B11):
 * finalizing then would silently kill the correction.
 */
export const canFinalizeNow = (match) =>
  !!match &&
  match.status === "completed" &&
  !(match.finalized_at || match.finalizedAt) &&
  match.score_correction_status !== "pending_votes";

/**
 * Doubles partners per side (MT-B14):
 * { participant1: [{ id, name }], participant2: [...] } or null for a singles match.
 * Prefers the backend's doubles_partners, then match_config.doubles_pairs /
 * badminton_doubles / table_tennis_doubles ({ pair_a, pair_b }).
 */
export const getDoublesPartners = (match) => {
  const fromApi = match?.doubles_partners || match?.doublesPartners;
  const valid = (list) => Array.isArray(list) && list.length > 0;
  if (fromApi && (valid(fromApi.participant1) || valid(fromApi.participant2))) {
    const norm = (list) =>
      (list || []).map((p) => ({
        id: p?.id ?? p?.user_id ?? null,
        name: p?.name || p?.display_name || p?.username || (p?.id != null ? `Player #${p.id}` : "Player"),
      }));
    return { participant1: norm(fromApi.participant1), participant2: norm(fromApi.participant2) };
  }
  const cfg = getMatchConfig(match);
  const pairs = cfg.doubles_pairs || cfg.badminton_doubles || cfg.table_tennis_doubles;
  if (!pairs || (!pairs.pair_a && !pairs.pair_b)) return null;
  const toList = (pair) =>
    [pair?.player_1_id, pair?.player_2_id]
      .filter((id) => id !== undefined && id !== null && id !== "")
      .map((id) => ({ id, name: `Player #${id}` }));
  return { participant1: toList(pairs.pair_a), participant2: toList(pairs.pair_b) };
};

/** Cancellation details, or null when the match is not cancelled. */
export const getCancellationInfo = (match) => {
  if (!match || match.status !== "cancelled") return null;
  return {
    reason: match.cancel_reason || match.cancelReason || null,
    at: match.cancelled_at || match.cancelledAt || null,
    by: match.cancelled_by_name || match.cancelled_by || match.cancelledBy || null,
  };
};
